import React, { useEffect, useState } from 'react';
import podiumService from '../../services/podiumService';

const GOLD = 'rgb(var(--brand-gold-rgb))';
const KIND_LABEL = { earnings_call: 'Earnings call', analyst_call: 'Analyst call', conference: 'Conference', investor_day: 'Investor day', other: 'Event' };
const mmss = s => (s == null ? '' : `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`);

// The person profile (slice 2): who they are, where they have been, every call they spoke on, the questions they asked
// or answered, the companies covered. Reached from any named speaker in a transcript — executives included.
export default function PodiumPersonView({ personKey, onBack, onOpenCall }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { setD(null); podiumService.getPerson(personKey).then(r => setD(r.data)).catch(() => setErr('unknown person')); }, [personKey]);
  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (!d) return <p className="text-sm text-gray-500">Loading…</p>;
  const p = d.person;
  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-xs uppercase tracking-[0.15em] text-gray-400 hover:text-gray-200">← back</button>
      <div>
        <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">{p.name}
          {p.spelling_unverified && <span className="ml-2 text-xs text-amber-500">spelling unverified</span>}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">{p.role_hint}{p.firm ? ` · ${p.firm}` : ''} · covers {d.companies.join(', ') || '—'} · {d.calls.length} call{d.calls.length === 1 ? '' : 's'} · {d.fingerprints.length} voice fingerprint{d.fingerprints.length === 1 ? '' : 's'}</p>
        {p.aliases?.length > 0 && <p className="text-xs text-gray-400">heard as: {p.aliases.join(', ')}</p>}
        {d.open_reviews.length > 0 && <p className="text-xs text-amber-500">{d.open_reviews.length} open review item{d.open_reviews.length > 1 ? 's' : ''} — Podium → Review</p>}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 text-sm">
          <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-gray-400">Affiliations</div>
          {d.affiliations.length === 0 && <p className="text-gray-500">none recorded</p>}
          {d.affiliations.map((a, i) => <div key={i}>{a.org}{a.role ? ` · ${a.role}` : ''} <span className="text-xs text-gray-400">{a.from || '?'}{a.to && a.to !== a.from ? ` → ${a.to}` : ''}</span></div>)}
        </div>
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 text-sm">
          <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-gray-400">Calls</div>
          {d.calls.map(c => (
            <div key={c.slug} className="flex flex-wrap items-baseline gap-2 border-t border-gray-100 py-1 first:border-0 dark:border-gray-800">
              <button onClick={() => onOpenCall(c.slug)} className="font-medium" style={{ color: GOLD }}>{c.ticker} · {c.fiscal_label} · {KIND_LABEL[c.kind] || c.kind}</button>
              <span className="text-xs text-gray-400">{c.held_at ? c.held_at.slice(0, 10) : ''} · {c.role || '—'} · {c.minutes} min</span>
            </div>))}
        </div>
      </div>
      {d.asked.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 text-sm space-y-2">
          <div className="text-[11px] uppercase tracking-[0.15em] text-gray-400">Questions asked</div>
          {d.asked.map((q, i) => (
            <div key={i} className="border-t border-gray-100 pt-2 first:border-0 first:pt-0 dark:border-gray-800">
              <button onClick={() => onOpenCall(q.call, q.at)} className="text-xs" style={{ color: GOLD }}>{q.ticker} {q.fiscal_label} · Q{q.n} · {mmss(q.at)}</button>
              <span className="ml-2 text-xs text-gray-400">answered by {q.answered_by.join(', ') || '—'}</span>
              {q.questions.map((t, j) => <p key={j} className="text-gray-700 dark:text-gray-300">{t}</p>)}
            </div>))}
        </div>)}
      {d.answered.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 text-sm space-y-2">
          <div className="text-[11px] uppercase tracking-[0.15em] text-gray-400">Questions answered</div>
          {d.answered.map((q, i) => (
            <div key={i} className="border-t border-gray-100 pt-2 first:border-0 first:pt-0 dark:border-gray-800">
              <button onClick={() => onOpenCall(q.call, q.at)} className="text-xs" style={{ color: GOLD }}>{q.ticker} {q.fiscal_label} · Q{q.n} · {mmss(q.at)}</button>
              <span className="ml-2 text-xs text-gray-400">asked by {q.asked_by}{q.firm ? ` (${q.firm})` : ''}</span>
              {q.questions.map((t, j) => <p key={j} className="text-gray-700 dark:text-gray-300">{t}</p>)}
            </div>))}
        </div>)}
    </div>
  );
}
